import { CommonModule } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Coding, CodeableConcept, Quantity, Range } from 'fhir/r4';
import { Observable, Subject, finalize, map, of, startWith, takeUntil, debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs';
import { FormFieldsSelectDataService } from '../shared/form-fields-select-data.service';
import { ObservationReferenceRange, ObservationReferenceRangeService, ObservationReferenceRangeTemplate } from './observation-reference-range.service';
import { LoadingUIEnabled } from '../loading.interceptor';

interface ReferenceRangeForm {
  id: FormControl<string | null>;
  code: FormControl<Coding | string | null>;
  display: FormControl<string>;
  type: FormControl<string>;
  interpretation: FormControl<string | null>;
  lowValue: FormControl<number | null>;
  lowUnit: FormControl<string>;
  highValue: FormControl<number | null>;
  highUnit: FormControl<string>;
  appliesTo: FormControl<CodeableConcept[]>;
  ageLowValue: FormControl<number | null>;
  ageLowUnit: FormControl<string>;
  ageHighValue: FormControl<number | null>;
  ageHighUnit: FormControl<string>;
  text: FormControl<string>;
}

interface ConceptOption {
  label: string;
  value: string;
  concept: CodeableConcept;
}

@Component({
  selector: 'app-observation-reference-ranges-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatChipsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSnackBarModule
  ],
  templateUrl: './observation-reference-ranges-dialog.component.html',
  styleUrls: ['./observation-reference-ranges-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ObservationReferenceRangesDialogComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly unitSystem = 'http://unitsofmeasure.org';
  private readonly maxAutocompleteResults = 50;

  readonly form: FormGroup<ReferenceRangeForm>;

  observationCodes: Coding[] = [];
  latestSearchMatches: Coding[] = [];
  filteredCodes$: Observable<Coding[]> = of([]);

  loadingCodes = false;
  savingTemplate = false;

  readonly rangeTypeOptions: ConceptOption[] = [
    this.buildRangeTypeOption('normal', 'Normal range'),
    this.buildRangeTypeOption('treatment', 'Critical / Needs Treatment'),
    this.buildRangeTypeOption('recommended', 'Recommended range'),
    this.buildRangeTypeOption('therapeutic', 'Therapeutic desired level')
  ];

  readonly interpretationOptions: ConceptOption[] = [
    this.buildInterpretationOption('N', 'Normal (N)'),
    this.buildInterpretationOption('H', 'High (H)'),
    this.buildInterpretationOption('L', 'Low (L)'),
    this.buildInterpretationOption('HH', 'Critical High (HH)'),
    this.buildInterpretationOption('LL', 'Critical Low (LL)'),
    this.buildInterpretationOption('A', 'Abnormal (A)'),
    this.buildInterpretationOption('AA', 'Critical Abnormal (AA)'),
    this.buildInterpretationOption('POS', 'Positive (POS)'),
    this.buildInterpretationOption('NEG', 'Negative (NEG)'),
    this.buildInterpretationOption('IND', 'Indeterminate (IND)'),
    this.buildInterpretationOption('RR', 'Reactive (RR)'),
    this.buildInterpretationOption('NR', 'Non-reactive (NR)'),
    this.buildInterpretationOption('S', 'Susceptible (S)'),
    this.buildInterpretationOption('R', 'Resistant (R)'),
    this.buildInterpretationOption('I', 'Intermediate (I)')
  ];

  readonly appliesToOptions: ConceptOption[] = [
    this.buildAppliesToOption('female', 'Female'),
    this.buildAppliesToOption('male', 'Male'),
    this.buildAppliesToOption('other', 'Other / Non-binary'),
    this.buildAppliesToOption('pregnant', 'Pregnant Patients')
  ];

  readonly ucumUnitOptions = ['mg/dL', 'mmol/L', 'g/L', 'mL', '%', 'U/L', 'x10^9/L', 'fl', 'g/L', 'pg/mL', 'ng/mL', 'mEq/L', 'mmHg', 'beats/min', 'breaths/min', 'Cel'];

  readonly ageUnitOptions = [
    { value: 'a', label: 'Years' },
    { value: 'mo', label: 'Months' },
    { value: 'wk', label: 'Weeks' },
    { value: 'd', label: 'Days' }
  ];

  readonly compareConcepts = (a?: CodeableConcept | null, b?: CodeableConcept | null) => {
    if (!a || !b) {
      return a === b;
    }
    const aCode = a.coding?.[0]?.code ?? a.text;
    const bCode = b.coding?.[0]?.code ?? b.text;
    return aCode === bCode;
  };

  constructor(
    private readonly fb: FormBuilder,
    private readonly selectDataService: FormFieldsSelectDataService,
    private readonly referenceRangesService: ObservationReferenceRangeService,
    private readonly snackBar: MatSnackBar,
    private readonly cdr: ChangeDetectorRef,
    private readonly http: HttpClient,
    public dialogRef: MatDialogRef<ObservationReferenceRangesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { template?: ObservationReferenceRangeTemplate }
  ) {
    this.form = this.buildForm();
    if (this.data?.template) {
      this.populateForm(this.data.template);
    }
  }

  ngOnInit(): void {
    this.loadObservationCodes();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get codeControl(): FormControl<Coding | string | null> {
    return this.form.controls.code;
  }

  displayCodeOption = (value: Coding | string | null): string => {
    if (!value) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    return value.display || value.code || '';
  };

  submitRange(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const template = this.mapFormToTemplate();
    if (!template) {
      this.showMessage('Please select a valid observation code.', 'error');
      return;
    }

    this.savingTemplate = true;
    this.cdr.markForCheck();
    this.referenceRangesService
      .saveTemplate({...template, _rawResource: this.data?.template?._rawResource})
      .pipe(
        finalize(() => {
          this.savingTemplate = false;
          console.log("this is the initial", this.data.template)
          console.log("this is the one going for saving", template)
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.showMessage('Reference range saved successfully.', 'success');
          this.dialogRef.close(true);
        },
        error: () => {
          this.showMessage('Failed to save reference range. Please try again.', 'error');
          this.cdr.markForCheck();
        }
      });
  }

  private buildForm(): FormGroup<ReferenceRangeForm> {
    return this.fb.group<ReferenceRangeForm>({
      id: this.fb.control<string | null>(null),
      code: this.fb.control<Coding | string | null>(null, { validators: Validators.required }),
      display: this.fb.nonNullable.control(''),
      type: this.fb.nonNullable.control('normal', { validators: Validators.required }),
        interpretation: this.fb.control<string | null>(null),
      lowValue: this.fb.control<number | null>(null),
      lowUnit: this.fb.nonNullable.control(''),
      highValue: this.fb.control<number | null>(null),
      highUnit: this.fb.nonNullable.control(''),
      appliesTo: this.fb.nonNullable.control<CodeableConcept[]>([]),
      ageLowValue: this.fb.control<number | null>(null),
      ageLowUnit: this.fb.nonNullable.control('a'),
      ageHighValue: this.fb.control<number | null>(null),
      ageHighUnit: this.fb.nonNullable.control('a'),
      text: this.fb.nonNullable.control('')
    });
  }

  private populateForm(template: ObservationReferenceRangeTemplate): void {
    const range = template.range;
    this.form.patchValue({
      id: template.id,
      code: template.code,
      display: template.display !== (template.code?.display || template.code?.code) ? (template.display ?? '') : '',
      type: this.resolveRangeType(range.type),
        interpretation: this.resolveInterpretation(range.interpretation),
      lowValue: range.low?.value ?? null,
      lowUnit: range.low?.unit || range.low?.code || '',
      highValue: range.high?.value ?? null,
      highUnit: range.high?.unit || range.high?.code || '',
      appliesTo: range.appliesTo ?? [],
      ageLowValue: range.age?.low?.value ?? null,
      ageLowUnit: range.age?.low?.unit || range.age?.low?.code || 'a',
      ageHighValue: range.age?.high?.value ?? null,
      ageHighUnit: range.age?.high?.unit || range.age?.high?.code || 'a',
      text: range.text ?? ''
    });
  }

  private loadObservationCodes(): void {
    this.loadingCodes = true;
    this.selectDataService
      .getFormFieldSelectData('observation', 'localCode')
      .pipe(
        finalize(() => {
          this.loadingCodes = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (codes: Coding[]) => {
          this.observationCodes = Array.isArray(codes) ? codes : [];
          this.filteredCodes$ = this.codeControl.valueChanges.pipe(
            startWith(this.codeControl.value ?? ''),
            map(value => typeof value === 'string' ? value : value?.display || value?.code || ''),
            debounceTime(300),
            distinctUntilChanged(),
            switchMap(search => this.searchCombinedCodes(search))
          );
          this.cdr.markForCheck();
        },
        error: () => {
          this.observationCodes = [];
          this.filteredCodes$ = of([]);
          this.showMessage('Unable to load local observation codes.');
          this.cdr.markForCheck();
        }
      });
  }

  private filterCodes(search: string): Coding[] {
    if (!this.observationCodes?.length) {
      return [];
    }
    const normalized = (search ?? '').toLowerCase();
    if (!normalized) {
      return this.observationCodes.slice(0, this.maxAutocompleteResults);
    }
    return this.observationCodes
      .filter(coding => (coding.display || '').toLowerCase().includes(normalized) || (coding.code || '').toLowerCase().includes(normalized))
      .slice(0, this.maxAutocompleteResults);
  }

  private searchCombinedCodes(search: string): Observable<Coding[]> {
    const localMatches = this.filterCodes(search);
    
    if (!search || search.trim().length < 2) {
      return of(localMatches);
    }

    const encoded = encodeURIComponent(search);
    const ontoUrl = `https://r4.ontoserver.csiro.au/fhir/ValueSet/$expand?url=http://terminology.hl7.org/ValueSet/snomed-intl-gps&_format=json&count=${this.maxAutocompleteResults}&filter=${encoded}`;
    
    const snomedSearch$ = this.http.get<any>(ontoUrl, { context: new HttpContext().set(LoadingUIEnabled, false) }).pipe(
      map(vs => {
        const contains = vs?.expansion?.contains || [];
        return contains.map((item: any) => ({
          system: item.system || 'http://snomed.info/sct',
          code: item.code,
          display: item.display
        } as Coding));
      }),
      catchError(() => of([] as Coding[]))
    );

    return snomedSearch$.pipe(
      map(snomedMatches => {
        const merged = [...localMatches, ...snomedMatches];
        const seen = new Set<string>();
        const unique: Coding[] = [];
        for (const c of merged) {
          const key = `${c.system}|${c.code}`;
          if (!seen.has(key)) {
            seen.add(key);
            unique.push(c);
          }
        }
        const results = unique.slice(0, this.maxAutocompleteResults);
        this.latestSearchMatches = results;
        return results;
      })
    );
  }

  private mapFormToTemplate(): ObservationReferenceRangeTemplate | null {
    const value = this.form.getRawValue();
    const coding = this.normalizeCoding(value.code);
    if (!coding) {
      return null;
    }

    const range: ObservationReferenceRange = {
      type: this.toRangeTypeConcept(value.type),
        interpretation: value.interpretation ? this.toInterpretationConcept(value.interpretation) : undefined,
      low: this.toQuantity(value.lowValue, value.lowUnit),
      high: this.toQuantity(value.highValue, value.highUnit),
      appliesTo: value.appliesTo?.length ? value.appliesTo : undefined,
      age: this.toAgeRange(value),
      text: value.text?.trim() || undefined
    };

    return {
      id: value.id || undefined,
      code: coding,
      display: value.display?.trim() || coding.display || coding.code,
      range
    };
  }

  private toQuantity(value: number | null, unit: string): Quantity | undefined {
    if (value == null || Number.isNaN(value)) {
      return undefined;
    }
    const trimmedUnit = unit?.trim();
    return {
      value,
      system: this.unitSystem,
      code: trimmedUnit || undefined,
      unit: trimmedUnit || undefined
    };
  }

  private toAgeRange(value: ReturnType<typeof this.form.getRawValue>): Range | undefined {
    const low = this.toQuantity(value.ageLowValue, value.ageLowUnit);
    const high = this.toQuantity(value.ageHighValue, value.ageHighUnit);
    if (!low && !high) {
      return undefined;
    }
    return { low, high };
  }

  private normalizeCoding(value: Coding | string | null): Coding | null {
    if (!value) {
      return null;
    }
    if (typeof value !== 'string') {
      return value;
    }
    const searchSpace = [...this.observationCodes, ...this.latestSearchMatches];
    const match = searchSpace.find(coding => {
      const display = coding.display?.toLowerCase() ?? '';
      const code = coding.code?.toLowerCase() ?? '';
      const search = value.toLowerCase();
      return display === search || code === search;
    });
    return match ?? null;
  }

  private toRangeTypeConcept(value: string | null): CodeableConcept | undefined {
    if (!value) {
      return undefined;
    }
    return this.rangeTypeOptions.find(option => option.value === value)?.concept;
  }

  private buildInterpretationOption(code: string, display: string): ConceptOption {
    return {
      label: display,
      value: code,
      concept: {
        coding: [{
          system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
          code,
          display
        }]
      }
    };
  }

  private resolveInterpretation(concept?: CodeableConcept): string | null {
    if (!concept) return null;
    const coding = concept.coding?.[0];
    return coding?.code ?? null;
  }

  private toInterpretationConcept(code: string): CodeableConcept {
    const option = this.interpretationOptions.find(o => o.value === code);
    if (option) return option.concept;
    return {
      coding: [{
        system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
        code
      }]
    };
  }

  private resolveRangeType(concept?: CodeableConcept): string {
    const code = concept?.coding?.[0]?.code;
    return this.rangeTypeOptions.find(option => option.concept.coding?.[0]?.code === code)?.value ?? 'normal';
  }

  private buildRangeTypeOption(code: string, label: string): ConceptOption {
    return {
      value: code,
      label,
      concept: {
        coding: [{
          system: 'http://terminology.hl7.org/CodeSystem/referencerange-meaning',
          code,
          display: label
        }],
        text: label
      }
    };
  }

  private buildAppliesToOption(code: string, label: string): ConceptOption {
    return {
      value: code,
      label,
      concept: {
        coding: [{
          system: 'http://hl7.org/fhir/administrative-gender',
          code,
          display: label
        }],
        text: label
      }
    };
  }

  private showMessage(message: string, severity?: 'error' | 'success'): void {
    const config: any = { duration: 3500 };
    if (severity === 'error') {
      config.panelClass = 'snackbar-error';
    } else if (severity === 'success') {
      config.panelClass = 'snackbar-success';
    }
    this.snackBar.open(message, 'Dismiss', config);
  }
}
